import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  ChevronRight,
  CheckCircle,
  AlertCircle,
  Mail,
} from "lucide-react";
import { RefreshCWIcon } from "../../components/icons";
import { pageRootTestIds } from "../../testing/pageRootTestIds";
import EmailPreviewModal from "./EmailPreviewModal";
import ConfirmSendModal from "./ConfirmSendModal";
import ArrowLeftIcon from "../../resources/icons/arrow-narrow-left.svg";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { GET, POST } from "../../service/CRUDService";
import EntityConstants from "../../configs/EntityConstants";
import Toast from "../../common/Toast/Toast";
import { emailccRegex } from "../../configs/App.Regex";
import CfLoader from "../../common/CfLoader/CfLoader";
import useNavigationStore from "../../store/navigationStore";
import { useTranslation } from "react-i18next";
import { handleRovingTabKeyDown } from "../../utils/keyboardNavigation";

const NotifyRecipients = () => {
  const { legacyTransactionId: paramLegacyTransactionId } = useParams();
  const {
    credential_template_id: paramCredentialTemplateId,
    issuer_credential_template_id: paramIssuerCredentialTemplateId,
  } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const currentProgramme = useNavigationStore(
    (state) => state.currentProgramme,
  );
  const currentBatch = useNavigationStore((state) => state.currentBatch);

  const issuerCredentialTemplateId =
    paramIssuerCredentialTemplateId ||
    location.state?.issuerCredentialTemplateId ||
    localStorage.getItem("issuerCredentialTemplateId");
  const credentialTemplateId =
    paramCredentialTemplateId ||
    location.state?.credentialTemplateId ||
    localStorage.getItem("credentialTemplateId");

  const [recipients, setRecipients] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [activeTab, setActiveTab] = useState("All Recipients");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [recipientsIssuedCount, setRecipientsIssuedCount] = useState(0);
  const [recipientsNotifiedCount, setRecipientsNotifiedCount] = useState(0);
  const [recipientsPendingCount, setRecipientsPendingCount] = useState(0);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [isMultipleSelect, setIsMultipleSelect] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [toEmails, setToEmails] = useState([]);
  const [currentToEmail, setCurrentToEmail] = useState("");
  const [toEmailError, setToEmailError] = useState("");
  const [certificateName, setCertificateName] = useState("");
  const [ccEmails, setCcEmails] = useState([]);
  const [currentCcEmail, setCurrentCcEmail] = useState("");
  const [ccEmailError, setCcEmailError] = useState("");
  const [credentialId, setCredentialId] = useState("");
  const [returnTemplateId, setReturnTemplateId] = useState("");
  const hasLoadedOnce = useRef(false);
  const [showConfirmSend, setShowConfirmSend] = useState(false);
  const [confirmSendData, setConfirmSendData] = useState(null);
  const [isSendingFromConfirm, setIsSendingFromConfirm] = useState(false);
  const [isNotifyAll, setIsNotifyAll] = useState(false);
  const legacyTransactionId =
    paramLegacyTransactionId || localStorage.getItem("legacyTransactionId");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    if (!issuerCredentialTemplateId) return;

    const fetchCertificateName = async () => {
      try {
        const res = await GET(
          `${EntityConstants.GET_USER_PREVIEW_SVG}?issuer_credential_template_id=${issuerCredentialTemplateId}`,
        );
        const data = await res.json();
        setCertificateName(data.certificate_name || "");
      } catch (error) {
        console.debug("Failed to fetch certificate name:", error);
      }
    };

    fetchCertificateName();
  }, [issuerCredentialTemplateId]);

  useEffect(() => {
    if (
      !currentProgramme ||
      !currentBatch ||
      !credentialTemplateId ||
      !issuerCredentialTemplateId
    ) {
      navigate("/issuer/workspace", { replace: true });
    }
  }, [
    currentProgramme,
    currentBatch,
    credentialTemplateId,
    issuerCredentialTemplateId,
    navigate,
  ]);

  const fetchRecipients = async (page, search = "") => {
    if (!issuerCredentialTemplateId) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      const offset = (page - 1) * itemsPerPage;
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : "";
      const notificationStatus =
        activeTab === "Notified"
          ? "&notification_status=sent"
          : activeTab === "Not Notified"
            ? "&notification_status=not_sent"
            : "&order_by_notification_status=not_sent";
      const statusFilter =
        "&status=Issued,Printed,Notified,Printed_and_Notified";

      const response = await GET(
        `${EntityConstants.GET_RECIPIENTS}/${issuerCredentialTemplateId}?limit=${itemsPerPage}&offset=${offset}${searchParam}${notificationStatus}${statusFilter}`,
      );

      const data = await response.json();

      const transformedRecipients = data.results.map((r) => {
        let displayStatus;
        switch (r.status) {
          case "Issued":
            displayStatus = t("workspace:pending");
            break;
          case "Printed":
            displayStatus = t("workspace:pending");
            break;
          case "Notified":
            displayStatus = t("workspace:notified");
            break;
          case "Revoked":
            displayStatus = t("workspace:pending");
            break;
          case "Failed":
            displayStatus = t("workspace:pending");
            break;
          case "Printed_and_Notified":
            displayStatus = t("workspace:notified");
            break;
          default:
            displayStatus = t("workspace:pending");
        }

        return {
          id: r.credential_id,
          name: r.recipient_name,
          email: r.recipient_email,
          rollNumber: r.roll_number,
          phone: r.recipient_phone,
          issuedOn: r.issued_on,
          notifiedAt: r.notified_at,
          status: displayStatus,
          previewUrl: r.credential_digital_preview,
        };
      });

      setRecipients(transformedRecipients);
      setTotalCount(data.count);
      setCredentialId(data.credential_id);
      hasLoadedOnce.current = true;
    } catch (error) {
      Toast({
        type: "error",
        title: t("workspace:error"),
        message: t("workspace:failed_fetch_recipients"),
      });
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleRefreshTable = async () => {
    setIsRefreshing(true);
    await fetchRecipients(currentPage, debouncedSearch);
    await fetchRecipientStats();
  };

  const fetchRecipientStats = async () => {
    if (!issuerCredentialTemplateId) {
      return;
    }
    try {
      const response = await GET(
        `${EntityConstants.GET_RECIPIENT_STATS}/${issuerCredentialTemplateId}`,
      );
      const data = await response.json();
      if (data.error) {
        Toast({
          type: "error",
          title: t("workspace:error"),
          message: data.error,
        });
      } else {
        setRecipientsIssuedCount(data.data.total_recipient);
        setRecipientsNotifiedCount(data.data.notified_recipient);
        setRecipientsPendingCount(data.data.pending_notifications);
      }
    } catch (error) {
      Toast({
        type: "error",
        title: t("workspace:error"),
        message: t("workspace:failed_fetch_transactions"),
      });
    }
  };

  useEffect(() => {
    if (searchQuery === debouncedSearch) return;

    const timeoutId = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  useEffect(() => {
    fetchRecipients(currentPage, debouncedSearch);
  }, [currentPage, debouncedSearch, activeTab, issuerCredentialTemplateId]);

  useEffect(() => {
    fetchRecipientStats();
  }, [issuerCredentialTemplateId]);

  useEffect(() => {
    if (!location.state?.reopenEmailPreviewModal) return;

    const templateIdFromReturn =
      location.state?.createdTemplateId ||
      location.state?.initialTemplateId ||
      "";
    const shouldOpenMultiple = Boolean(location.state?.isMultipleSelect);
    const returningRecipientIds = location.state?.selectedRecipientIds || [];
    const returningCredentialId =
      location.state?.reopenCredentialId || returningRecipientIds[0];

    const clearReturnState = () => {
      const restState = { ...(location.state || {}) };
      delete restState.reopenEmailPreviewModal;
      delete restState.createdTemplateId;
      delete restState.initialTemplateId;
      delete restState.reopenCredentialId;
      delete restState.selectedRecipientIds;
      delete restState.isMultipleSelect;
      navigate(location.pathname, { replace: true, state: restState });
    };

    if (shouldOpenMultiple && returningRecipientIds.length > 0) {
      const matchedRecipients = recipients.filter((item) =>
        returningRecipientIds.includes(item.id),
      );
      if (matchedRecipients.length < 1) {
        if (!isLoading && hasLoadedOnce.current) clearReturnState();
        return;
      }

      setSelectedRecipients(matchedRecipients);
      setSelectedIds(returningRecipientIds);
      setSelectedRecipient(null);
      setIsMultipleSelect(true);
      setToEmails([...new Set(matchedRecipients.map((item) => item.email))]);
      setCurrentToEmail("");
      setToEmailError("");
      setCcEmails([]);
      setCurrentCcEmail("");
      setCcEmailError("");
      setReturnTemplateId(templateIdFromReturn);
      setIsModalOpen(true);
      clearReturnState();
      return;
    }

    if (returningCredentialId) {
      const matchedRecipient = recipients.find(
        (item) => item.id === returningCredentialId,
      );
      if (!matchedRecipient) {
        if (!isLoading && hasLoadedOnce.current) clearReturnState();
        return;
      }

      setSelectedRecipient(matchedRecipient);
      setSelectedRecipients([]);
      setIsMultipleSelect(false);
      setToEmails(matchedRecipient.email ? [matchedRecipient.email] : []);
      setCurrentToEmail("");
      setToEmailError("");
      setCcEmails([]);
      setCurrentCcEmail("");
      setCcEmailError("");
      setReturnTemplateId(templateIdFromReturn);
      setIsModalOpen(true);
      clearReturnState();
    }
  }, [location.state, recipients, navigate, location.pathname, isLoading]);

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const handleSelectAll = useCallback(
    (selected) => {
      if (selected) {
        setSelectedIds(recipients.map((r) => r.id));
      } else {
        setSelectedIds([]);
      }
    },
    [recipients],
  );

  const handleSelectOne = useCallback((id, selected) => {
    if (selected) {
      setSelectedIds((prev) => [...prev, id]);
    } else {
      setSelectedIds((prev) => prev.filter((prevId) => prevId !== id));
    }
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    setSelectedIds([]);
  }, []);

  const handleNotifySelected = useCallback(() => {
    if (selectedIds.length === 0) return;

    const selectedRecipientsData = recipients.filter((r) =>
      selectedIds.includes(r.id),
    );
    setSelectedRecipients(selectedRecipientsData);
    const initialToEmails = [
      ...new Set(selectedRecipientsData.map((r) => r.email)),
    ];
    setToEmails(initialToEmails);
    setReturnTemplateId("");
    setIsMultipleSelect(true);
    setIsNotifyAll(false);
    setIsModalOpen(true);
  }, [selectedIds, recipients]);

  const handleNotifyAll = useCallback(() => {
    if (recipients.length === 0) return;
    const firstRecipient = recipients[0];
    setSelectedRecipient(firstRecipient);
    setSelectedRecipients([]);
    setIsMultipleSelect(false);
    setIsNotifyAll(true);
    setToEmails(firstRecipient.email ? [firstRecipient.email] : []);
    setReturnTemplateId("");
    setIsModalOpen(true);
  }, [recipients]);

  const handleRowClick = useCallback((recipient) => {
    setSelectedRecipient(recipient);
    setIsMultipleSelect(false);
    setToEmails([...new Set([recipient.email])]);
    setReturnTemplateId("");
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedRecipients([]);
    setSelectedRecipient(null);
    setIsMultipleSelect(false);
    setIsNotifyAll(false);
    setToEmails([]);
    setCurrentToEmail("");
    setToEmailError("");
    setCcEmails([]);
    setCurrentCcEmail("");
    setCcEmailError("");
    setReturnTemplateId("");
  }, []);

  const handleRequestConfirmation = useCallback((data) => {
    setIsModalOpen(false);
    setConfirmSendData(data);
    setTimeout(() => setShowConfirmSend(true), 250);
  }, []);

  const handleConfirmSend = useCallback(async () => {
    if (!confirmSendData) return;

    if (confirmSendData.isNotifyAll) {
      try {
        setIsSendingFromConfirm(true);
        const payload = {
          notification_type: "email",
          email_template: confirmSendData.selectedTemplateId || "",
          issuer_credential_template_id: issuerCredentialTemplateId,
        };
        const response = await POST(EntityConstants.SEND_EMAIL, payload);
        const data = await response.json();
        if (response.ok) {
          Toast({
            type: "success",
            title: t("workspace:success"),
            message: t("workspace:notify_all_sent_successfully"),
          });
          setShowConfirmSend(false);
          setConfirmSendData(null);
          setSelectedRecipients([]);
          setSelectedRecipient(null);
          setIsMultipleSelect(false);
          setIsNotifyAll(false);
          setSelectedIds([]);
          setToEmails([]);
          setCurrentToEmail("");
          setToEmailError("");
          setCcEmails([]);
          setCurrentCcEmail("");
          setCcEmailError("");
          setReturnTemplateId("");
          handleRefreshTable();
        } else {
          throw new Error(data.message || t("workspace:failed_send_emails"));
        }
      } catch (error) {
        Toast({
          type: "error",
          title: t("workspace:error"),
          message: error?.message || t("workspace:failed_send_emails"),
        });
      } finally {
        setIsSendingFromConfirm(false);
      }
      return;
    }

    if (!confirmSendData?.sendPayload) return;
    try {
      setIsSendingFromConfirm(true);
      const response = await POST(
        EntityConstants.SEND_EMAIL,
        confirmSendData.sendPayload,
      );
      const data = await response.json();
      if (response.ok) {
        Toast({
          type: "success",
          title: t("workspace:success"),
          message: t("workspace:emails_sent_successfully"),
        });
        setShowConfirmSend(false);
        setConfirmSendData(null);
        // Full cleanup
        setSelectedRecipients([]);
        setSelectedRecipient(null);
        setIsMultipleSelect(false);
        setIsNotifyAll(false);
        setSelectedIds([]);
        setToEmails([]);
        setCurrentToEmail("");
        setToEmailError("");
        setCcEmails([]);
        setCurrentCcEmail("");
        setCcEmailError("");
        setReturnTemplateId("");
        handleRefreshTable();
      } else {
        throw new Error(data.message || t("workspace:failed_send_emails"));
      }
    } catch (error) {
      Toast({
        type: "error",
        title: t("workspace:error"),
        message: error?.message || t("workspace:failed_send_emails"),
      });
    } finally {
      setIsSendingFromConfirm(false);
    }
  }, [confirmSendData, t, issuerCredentialTemplateId]);

  const handleCancelConfirm = useCallback(() => {
    setShowConfirmSend(false);
    setConfirmSendData(null);
    setTimeout(() => setIsModalOpen(true), 250);
  }, []);

  const handleToEmailChange = (e) => {
    const email = e.target.value;
    setCurrentToEmail(email);
    if (!email || emailccRegex.test(email)) {
      setToEmailError("");
    }
  };

  const handleAddToEmail = () => {
    if (emailccRegex.test(currentToEmail)) {
      if (toEmails.includes(currentToEmail)) {
        setToEmailError(`* ${t("workspace:email_already_exists")}`);
      } else {
        setToEmails([...toEmails, currentToEmail]);
        setCurrentToEmail("");
        setToEmailError("");
      }
    } else if (currentToEmail) {
      setToEmailError(`* ${t("workspace:please_enter_valid_email")}`);
    }
  };

  const handleRemoveToEmail = (email) => {
    setToEmails(toEmails.filter((item) => item !== email));
    setToEmailError("");
  };

  const handlePasteToEmail = (event) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData("text");
    const emailMatches =
      pastedText.match(new RegExp(emailccRegex.source, "g")) || [];
    const uniqueEmails = [
      ...new Set(emailMatches.map((email) => email.trim())),
    ];

    uniqueEmails.forEach((email) => {
      if (toEmails.includes(email)) {
        setToEmailError(
          `* ${t("workspace:email")} "${email}" ${t(
            "workspace:already_exists",
          )}`,
        );
      } else {
        setToEmails((prev) => [...prev, email]);
      }
    });

    const invalidEmails = pastedText
      .split(/[,\s]+/)
      .map((email) => email.trim())
      .filter(
        (email) =>
          email && !emailccRegex.test(email) && !uniqueEmails.includes(email),
      );

    if (invalidEmails.length > 0) {
      setToEmailError(
        `${t("workspace:invalid_emails_list")} ${invalidEmails.join(", ")}`,
      );
    } else {
      setToEmailError("");
    }

    setCurrentToEmail("");
  };

  // Similar handlers for CC emails
  const handleCcEmailChange = (e) => {
    const email = e.target.value;
    setCurrentCcEmail(email);
    if (!email || emailccRegex.test(email)) {
      setCcEmailError("");
    }
  };

  const handleAddCcEmail = () => {
    if (emailccRegex.test(currentCcEmail)) {
      if (ccEmails.includes(currentCcEmail)) {
        setCcEmailError(`* ${t("workspace:email_already_exists")}`);
      } else {
        setCcEmails([...ccEmails, currentCcEmail]);
        setCurrentCcEmail("");
        setCcEmailError("");
      }
    } else if (currentCcEmail) {
      setCcEmailError(`* ${t("workspace:please_enter_valid_email")}`);
    }
  };
  const handlePendingIssuanceClick = () => {
    const summaryTransactionId =
      legacyTransactionId || issuerCredentialTemplateId;
    navigate(
      `/issuer/workspace/batches/summary/${credentialTemplateId}/${summaryTransactionId}`,
    );
  };

  const handleBackToSummary = useCallback(() => {
    navigate(
      `/issuer/workspace/batches/summary/${credentialTemplateId}/${issuerCredentialTemplateId}`,
    );
  }, [credentialTemplateId, issuerCredentialTemplateId, navigate]);

  const handleBatchClick = () => {
    navigate(`/issuer/workspace/batches/credentials`);
  };
  const handleWorkspaceClick = () => {
    navigate("/issuer/workspace");
  };
  const handleProgrammeClick = () => {
    navigate(`/issuer/workspace/batches/${currentProgramme?.programme_code}`);
  };

  const handleRemoveCcEmail = (email) => {
    setCcEmails(ccEmails.filter((item) => item !== email));
    setCcEmailError("");
  };

  const handlePasteCcEmail = (event) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData("text");
    const emailMatches = pastedText.match(emailccRegex) || [];
    const uniqueEmails = [
      ...new Set(emailMatches.map((email) => email.trim())),
    ];

    uniqueEmails.forEach((email) => {
      if (ccEmails.includes(email)) {
        setCcEmailError(
          `* ${t("workspace:email")} "${email}" ${t(
            "workspace:already_exists",
          )}`,
        );
      } else {
        setCcEmails((prev) => [...prev, email]);
      }
    });

    const invalidEmails = pastedText
      .split(/[,\s]+/)
      .map((email) => email.trim())
      .filter(
        (email) =>
          email && !emailccRegex.test(email) && !uniqueEmails.includes(email),
      );

    if (invalidEmails.length > 0) {
      setCcEmailError(
        `${t("workspace:invalid_emails_list")} ${invalidEmails.join(", ")}`,
      );
    } else {
      setCcEmailError("");
    }

    setCurrentCcEmail("");
  };

  const formatHeaderText = (key) => {
    return key
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .toUpperCase();
  };

  const fixedHeaders = [
    { key: "name", display: t("workspace:email_table_name") },
    { key: "email", display: t("workspace:email_table_email") },
    { key: "phone", display: t("workspace:email_table_phone") },
    { key: "issuedOn", display: t("workspace:email_table_issued_on") },
    { key: "notifiedAt", display: t("workspace:email_table_notify_at") },
    { key: "status", display: t("workspace:email_table_status") },
  ];

  const handleSendEmail = useCallback(
    async (subject, body) => {
      setIsLoading(true);
      try {
        const recipientIds = isMultipleSelect
          ? selectedIds
          : [selectedRecipient.id];

        const payload = {
          transaction_log_id: issuerCredentialTemplateId,
          credential_ids: recipientIds,
          to_emails: toEmails,
          cc_emails: ccEmails,
          subject: subject,
          body: body,
        };

        const response = await POST(
          EntityConstants.POST_SEND_NOTIFICATIONS,
          payload,
        );
        const data = await response.json();

        if (response.ok) {
          Toast({
            type: "success",
            title: t("workspace:success"),
            message:
              data.message || t("workspace:notifications_sent_successfully"),
          });
          handleCloseModal();
        } else {
          throw new Error(data.message || t("workspace:failed_notification"));
        }
      } catch (error) {
        Toast({
          type: "error",
          title: t("workspace:error"),
          message: error.message || t("workspace:unexpected_error_occurred"),
        });
      } finally {
        setIsLoading(false);
      }
    },
    [
      issuerCredentialTemplateId,
      selectedIds,
      selectedRecipient,
      toEmails,
      ccEmails,
      isMultipleSelect,
      handleCloseModal,
    ],
  );

  const tabOptions = [
    t("workspace:all_recipients"),
    t("workspace:notified"),
    t("workspace:not_notified"),
  ];

  const handleKeyDown = (event, action) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      action();
    }
  };

  const handleRecipientTabKeyDown = (event, tab) => {
    handleRovingTabKeyDown({
      event,
      items: tabOptions,
      currentItem: tab,
      onChange: (nextTab) => {
        setActiveTab(nextTab);
        setCurrentPage(1);
      },
      getElementId: (_nextTab, nextIndex) =>
        `notify-recipients-tab-${nextIndex}`,
    });
  };

  return (
    <div
      className="w-full mt-20 bg-cfSecondary min-h-screen"
      id={pageRootTestIds.notifyRecipients}
    >
      {/* Breadcrumbs */}
      <div
        className={`w-full bg-cfSearchContainer flex justify-center py-[0.1rem] px-4`}
      >
        <div
          className={`flex flex-col lg:flex-row items-start lg:items-center min-h-[4rem] justify-between w-full py-2`}
        >
          <div className="min-w-0 w-full">
            <div className="flex items-center gap-2 text-cfHeadingText font-semibold text-lg self-start my-auto">
              <h2 className="text-lg font-medium flex items-center justify-start min-h-[2.5rem] lg:min-h-[4rem] w-full min-w-0">
                <div className="flex items-center gap-2 text-sm text-gray-500 truncate min-w-0 max-w-full">
                  <button
                    id="notify-recipients-breadcrumb-workspace"
                    onClick={handleWorkspaceClick}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleWorkspaceClick();
                      }
                    }}
                    className="hover:text-gray-800 cursor-pointer focus:outline-none"
                  >
                    {t("workspace:workspace")}
                  </button>
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-cfTextBreadcrumbDisabled" />
                  <span
                    id="notify-recipients-breadcrumb-programme"
                    onClick={handleProgrammeClick}
                    onKeyDown={(e) => handleKeyDown(e, handleProgrammeClick)}
                    tabIndex={0}
                    role="button"
                    aria-label="Navigate to programme"
                    className="hover:text-gray-800 cursor-pointer focus:outline-none breadcrumb-item"
                    title={
                      currentProgramme?.programme_name ||
                      t("workspace:programme")
                    }
                  >
                    {currentProgramme?.programme_name ||
                      t("workspace:programme")}
                  </span>
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-cfTextBreadcrumbDisabled" />
                  <span
                    id="notify-recipients-breadcrumb-batch"
                    onClick={handleBatchClick}
                    onKeyDown={(e) => handleKeyDown(e, handleBatchClick)}
                    role="button"
                    tabIndex={0}
                    className="hover:text-gray-800 cursor-pointer focus:outline-none breadcrumb-item"
                    title={currentBatch?.batch_name || t("workspace:batch")}
                  >
                    {currentBatch?.batch_name || t("workspace:batch")}
                  </span>
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-cfTextBreadcrumbDisabled" />
                  <span
                    id="notify-recipients-breadcrumb-certificate"
                    onClick={handlePendingIssuanceClick}
                    onKeyDown={(e) =>
                      handleKeyDown(e, handlePendingIssuanceClick)
                    }
                    role="button"
                    tabIndex={0}
                    className="hover:text-gray-800 cursor-pointer focus:outline-none breadcrumb-item"
                    title={certificateName}
                  >
                    {certificateName ? certificateName : t("workspace:loading")}
                  </span>
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-cfTextBreadcrumbDisabled" />
                  <span className="text-gray-800 font-medium">
                    {t("workspace:notify_recipients")}
                  </span>
                </div>
              </h2>
            </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6 mt-8 my-4 px-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <button
              id="notify-recipients-back"
              onClick={handleBackToSummary}
              onKeyDown={(e) => handleKeyDown(e, handleBackToSummary)}
              aria-label="Go back to summary"
              className="cursor-pointer focus:outline-none"
            >
              <img src={ArrowLeftIcon} alt="Back" className="h-5 w-5" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900">
              {t("workspace:notify_recipients")}
            </h1>
          </div>
          <p className="text-gray-600">
            {t("workspace:send_notifications_to_certificate_recipients")}
          </p>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 my-4 px-4">
        <div
          className="bg-white rounded-lg p-4 border border-gray-200 flex flex-col"
          id="notify-recipients-stat-total"
        >
          <span className="text-gray-500 text-sm mb-1">
            {t("workspace:total_recipients")}
          </span>
          <div className="flex justify-between items-center">
            <span className="text-2xl font-bold">{recipientsIssuedCount}</span>
          </div>
        </div>
        <div
          className="bg-white rounded-lg p-4 border border-gray-200 flex flex-col"
          id="notify-recipients-stat-notified"
        >
          <span className="text-gray-500 text-sm mb-1">
            {t("workspace:notified")}
          </span>
          <div className="flex justify-between items-center">
            <span className="text-2xl font-bold">
              {recipientsNotifiedCount}
            </span>
            <CheckCircle className="h-6 w-6 text-green-500" />
          </div>
        </div>
        <div
          className="bg-white rounded-lg p-4 border border-gray-200 flex flex-col"
          id="notify-recipients-stat-pending"
        >
          <span className="text-gray-500 text-sm mb-1">
            {t("workspace:pending_notification")}
          </span>
          <div className="flex justify-between items-center">
            <span className="text-2xl font-bold">{recipientsPendingCount}</span>
            <AlertCircle className="h-6 w-6 text-amber-400" />
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-4 my-4 px-4 overflow-x-auto">
        <div
          className="flex -mb-px min-w-max"
          role="tablist"
          aria-label={t("workspace:recipients")}
        >
          {tabOptions.map((tab, tabIndex) => (
            <button
              key={tab}
              id={`notify-recipients-tab-${tabIndex}`}
              role="tab"
              aria-selected={activeTab === tab}
              tabIndex={activeTab === tab ? 0 : -1}
              className={`py-2 px-4 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-2 ${
                activeTab === tab
                  ? "border-b-2 border-blue-900 text-cfPrimaryButton"
                  : "text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 border-transparent"
              } transition-colors duration-200`}
              onClick={() => {
                setActiveTab(tab);
                setCurrentPage(1);
              }}
              onKeyDown={(e) => handleRecipientTabKeyDown(e, tab)}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Action Toolbar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4 my-4 px-4">
        <div className="relative w-full md:w-auto">
          <input
            id="notify-recipients-input-search"
            type="text"
            placeholder={t("workspace:search_recipients")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 py-2 pr-3 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full md:w-64"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            id="notify-recipients-refresh"
            onClick={handleRefreshTable}
            disabled={isLoading || isRefreshing}
            className="px-3 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
          >
            <RefreshCWIcon
              size={16}
              className={`inline-flex ${isRefreshing ? "animate-spin" : ""}`}
              aria-hidden
            />
            <span>{t("workspace:refresh")}</span>
          </button>
          <div className="relative">
            {selectedIds.length > 0 ? (
              <button
                id="notify-recipients-notify-selected"
                onClick={handleNotifySelected}
                className="flex items-center gap-2 px-4 py-[0.66rem] rounded-md text-sm font-medium text-white bg-cfButton hover:bg-cfPrimaryDark transition-all duration-300 ease-in-out shadow-sm hover:shadow-md"
              >
                <Mail className="h-4 w-4" />
                <span>{t("workspace:notify_selected")}</span>
              </button>
            ) : (
              <button
                id="notify-recipients-notify-all"
                onClick={handleNotifyAll}
                disabled={isLoading || recipients.length === 0}
                className={`flex items-center gap-2 px-4 py-[0.66rem] rounded-md text-sm font-medium text-white transition-all duration-300 ease-in-out shadow-sm hover:shadow-md ${
                  isLoading || recipients.length === 0
                    ? "bg-[#8d8fd6] cursor-not-allowed opacity-75"
                    : "bg-cfButton hover:bg-cfPrimaryDark"
                }`}
              >
                <Mail className="h-4 w-4" />
                <span>{t("workspace:notify_all")}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Recipients Table */}
      <div className="m-4 overflow-x-auto border border-gray-200 rounded-lg">
        <table
          className="w-full table-auto min-w-[74.5rem]"
          id="notify-recipients-table"
        >
          <thead className="bg-cfTableHeaderBackgroundColor h-[3.563rem] text-xs font-medium">
            <tr className="text-center">
              <th className="px-3 py-3">
                <input
                  id="notify-recipients-select-all"
                  type="checkbox"
                  checked={
                    recipients.length > 0 &&
                    selectedIds.length === recipients.length
                  }
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </th>
              {/* Show fixed headers when loading or no recipients, otherwise dynamic headers */}
              {isLoading || recipients.length === 0 ? (
                <>
                  {fixedHeaders.map((header) => (
                    <th key={header.key} className="px-4 py-3">
                      {header.display}
                    </th>
                  ))}
                </>
              ) : (
                Object.keys(recipients[0])
                  .filter((key) => key !== "id" && key !== "previewUrl") //! Exclude these fields in table header(future reference)
                  .map((key) => (
                    <th key={key} className="px-4 py-3">
                      {formatHeaderText(key)}
                    </th>
                  ))
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td
                  colSpan={fixedHeaders.length + 1}
                  className="py-20 text-center"
                >
                  <div className="flex justify-center items-center">
                    <CfLoader />
                  </div>
                </td>
              </tr>
            ) : recipients.length === 0 ? (
              <tr className="text-center">
                <td
                  colSpan={fixedHeaders.length + 1}
                  className="text-center py-4"
                  id="notify-recipients-empty"
                >
                  {t("workspace:no_recipients_found")}
                </td>
              </tr>
            ) : (
              recipients.map((recipient) => (
                <tr
                  key={recipient.id}
                  id={`notify-recipients-row-${recipient.id}`}
                  className="text-center hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
                  onClick={() => handleRowClick(recipient)}
                  onKeyDown={(e) =>
                    handleKeyDown(e, () => handleRowClick(recipient))
                  }
                  tabIndex={0}
                  role="button"
                  aria-label={`View details for ${recipient.name}`}
                >
                  <td
                    className="px-2 py-3"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      id={`notify-recipients-checkbox-${recipient.id}`}
                      type="checkbox"
                      checked={selectedIds.includes(recipient.id)}
                      onChange={(e) =>
                        handleSelectOne(recipient.id, e.target.checked)
                      }
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </td>
                  {Object.entries(recipient)
                    .filter(([key]) => key !== "id" && key !== "previewUrl") //! Exclude these fields in table body(future reference)
                    .map(([key, value]) => {
                      const cellId = `notify-recipients-row-${recipient.id}-${key}`;
                      if (key === "status") {
                        return (
                          <td key={key} id={cellId} className="px-4 py-3">
                            {value === "Notified" ? (
                              <div className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-md text-sm font-medium bg-green-100 text-green-800">
                                <CheckCircle className="h-4 w-4" />
                                <span>{t("workspace:notified")}</span>
                              </div>
                            ) : (
                              <div className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-md text-sm font-medium bg-amber-100 text-amber-800">
                                <AlertCircle className="h-4 w-4" />
                                <span>{t("workspace:pending")}</span>
                              </div>
                            )}
                          </td>
                        );
                      }

                      return (
                        <td key={key} id={cellId} className="px-4 py-3 text-gray-600">
                          {value || "N/A"}
                        </td>
                      );
                    })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between pb-6 px-4 w-full">
        <button
          id="notify-recipients-pagination-prev"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
        >
          <span className="flex items-center justify-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="w-5 h-5 rtl:-scale-x-100"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
              />
            </svg>

            <span>{t("workspace:previous")}</span>
          </span>
        </button>

        <button
          id="notify-recipients-pagination-next"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
        >
          <span className="flex items-center justify-center gap-2">
            <span>{t("workspace:next")}</span>

            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="w-5 h-5 rtl:-scale-x-100"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
              />
            </svg>
          </span>
        </button>
      </div>

      {/* Email Preview Modal */}
      {isModalOpen && (
        <EmailPreviewModal
          recipient={isMultipleSelect ? null : selectedRecipient}
          recipients={isMultipleSelect ? selectedRecipients : []}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          isMultipleSelect={isMultipleSelect}
          isNotifyAll={isNotifyAll}
          toEmails={toEmails}
          currentToEmail={currentToEmail}
          toEmailError={toEmailError}
          onToEmailChange={handleToEmailChange}
          onAddToEmail={handleAddToEmail}
          onRemoveToEmail={handleRemoveToEmail}
          onPasteToEmail={handlePasteToEmail}
          ccEmails={ccEmails}
          setCcEmails={setCcEmails}
          currentCcEmail={currentCcEmail}
          ccEmailError={ccEmailError}
          onCcEmailChange={handleCcEmailChange}
          onAddCcEmail={handleAddCcEmail}
          onRemoveCcEmail={handleRemoveCcEmail}
          onPasteCcEmail={handlePasteCcEmail}
          onSendEmail={handleSendEmail}
          credentialId={credentialId}
          initialTemplateId={returnTemplateId}
          issuerCredentialTemplateId={issuerCredentialTemplateId}
          onRequestConfirmation={handleRequestConfirmation}
          recipientsPendingCount={recipientsPendingCount}
          recipientsIssuedCount={recipientsIssuedCount}
        />
      )}

      <ConfirmSendModal
        isOpen={showConfirmSend}
        onClose={handleCancelConfirm}
        onConfirm={handleConfirmSend}
        recipientCount={confirmSendData?.recipientCount || 0}
        totalTo={confirmSendData?.totalTo || 0}
        totalCc={confirmSendData?.totalCc || 0}
        isSending={isSendingFromConfirm}
        isNotifyAll={confirmSendData?.isNotifyAll || false}
      />
    </div>
  );
};

export default NotifyRecipients;
